import { Component, OnInit, Input, ElementRef, ViewChild, OnChanges, AfterViewInit } from '@angular/core';
import * as d3 from 'd3';

import { Partition } from 'src/app/shared/models/classes/simulation/partition';
import { FormatService } from 'src/app/shared/services/format.service';
import { UiService } from 'src/app/shared/services/ui.service';
import { IndiceModeType } from 'src/app/shared/models/enums/indice-mode-type.enum';

@Component({
  selector: 'app-citizen-partition-chart',
  templateUrl: './citizen-partition-chart.component.html',
  styleUrls: ['./citizen-partition-chart.component.scss']
})
export class CitizenPartitionChartComponent implements OnInit, AfterViewInit, OnChanges {
    @Input() mainPartition: Partition;
    @Input() chartSize: number;

    // Trick to set element ref inside ngif after test become true (https://angular.io/api/core/ViewChild)
    private chartContainer: ElementRef;

    @ViewChild('sunburst') set content(content: ElementRef) {
        this.chartContainer = content;
    }

    colors: string[];

    colorsTree: {
        code: string,
        color: string
    }[] = [];

    vis: any;
    partition = null;
    arc = null;
    radius: number;

    showedLabel: string;
    showedValue: string;

    constructor(
        private formatSrv: FormatService,
        private uiSrv: UiService
    ) { }

    ngOnInit() {
    }

    ngAfterViewInit() {
        this.initChart();
    }

    ngOnChanges() {
        this.initChart();
    }

    private initChart(): void {
        this.colors = this.uiSrv.getChartsColors();

        if(this.mainPartition) {
            this.setPartsColors(this.mainPartition.partitions);
            this.drawSunburst();
        } else {
            this.cleanUpChart();
        }
    }

    // create base chart objects with D3.js
    private drawSunburst(): void {
        const element = this.chartContainer.nativeElement;

        this.cleanUpChart();

        this.radius = Math.min(element.offsetWidth, element.offsetWidth) / 2;

        const svg = d3.select(element).append("svg:svg")
        .attr("width", element.offsetWidth)
        .attr("height", element.offsetWidth);

        this.vis = svg.append("svg:g")
        .attr("id", `container`)
        .attr("transform", "translate(" + element.offsetWidth / 2 + "," + element.offsetWidth / 2 + ")");

        this.partition = d3.layout.partition()
        .size([2 * Math.PI, this.radius * this.radius])
        .value(d => d.ratio * 100)
        .children(d => d.partitions)
        .sort(null);

        this.arc = d3.svg.arc()
        .startAngle(d => d.x)
        .endAngle(d => d.x + d.dx)
        .innerRadius(d => Math.sqrt(d.y) - 5)
        .outerRadius(d => Math.sqrt(d.y + d.dy));

        this.createVisualization();
    }

    // Main function to draw and set up the visualization, once we have the data.
    private createVisualization(): void {
        const json = JSON.parse(JSON.stringify(this.mainPartition));
        const nodes = this.partition.nodes(json);

        // Bounding circle underneath the sunburst, to make it easier to detect
        // when the mouse leaves the parent g.
        this.vis
        .append("svg:circle")
        .attr("r", this.radius - this.radius * 0.32)
        .attr("id", `chart-bg`)
        .style("opacity", 0);

        this.vis
        .append("svg:circle")
        .attr("r", this.radius - this.radius * 0.38)
        .attr("id", `text-bg`)
        .style("opacity", 0);

        this.vis.data([json]).selectAll("path")
        .data(nodes)
        .enter().append("svg:path")
        .attr("display", d => (d.depth && d.isVisible) ? null : "none")
        .attr("d", this.arc)
        .attr("fill-rule", "inherit")
        .style("fill", d => this.fillColor(d));
        // .style('stroke', '#FFF');

        this.setActions();
    }

    // Manage mouseleave/mouseover handlers on circles.
    private setActions() {
        this.vis.selectAll("path")
        .on("mouseover", d => this.highlightPath(d))
        .on("click", d => this.highlightPath(d));
    }

    // Set the color of each node
    private fillColor(d: any) {
        const found = this.colorsTree.find(color => color.code === d.partitionDetails.code);
        if(found) {
            return found.color;
        }
    }

    private cleanUpChart() {
        const element = this.chartContainer.nativeElement;
        d3.select(element.querySelector('svg')).remove();
    }

    // Fade all but the current sequence.
    private highlightPath(selected: any): void {
        this.showedLabel = selected.partitionDetails.text ? selected.partitionDetails.text.shortLabel : "Text not found";

        if(this.mainPartition.mode === IndiceModeType.amount) {
            this.showedValue = this.formatSrv.formatAmount(100 * selected.ratio) + " €";
        } else {
            this.showedValue = this.formatSrv.formatPercent(selected.ratio) + " %";
        }

        this.vis.select("#chart-bg")
        .style("opacity", 1)
        .style("fill", this.fillColor(selected));

        this.vis.select("#text-bg")
        .style("opacity", 1)
        .style("fill", "#FFF");

        // Fade all the segments.
        this.vis.selectAll("path").style("opacity", 0.3);
        // this.vis.selectAll("path").style('stroke', '#FFF');

        // Then highlight only those that are an ancestor of the current segment.
        this.vis.selectAll("path")
        .filter(function(node) {
            const path = [];
            let current = selected;

            while (current.parent) {
                path.unshift(current);
                current = current.parent;
            }

            return (path.indexOf(node) >= 0);
        })
        .style("opacity", 1);
    }

    // Create gradient colors array to apply in chart for each partition
    private setPartsColors(partitions: Partition[], sendColor?: string): void {
        partitions.map((partition, index) => {
            const color = sendColor || this.colors[index];
            this.colorsTree.push({
                code: partition.partitionDetails.code,
                color: color
            });

            if(partition.partitions && partition.partitions.length) {
                // const lighten = this.lightenHexColor(color, 10);

                this.setPartsColors(partition.partitions, color);
            }
        });
    }
}
